import { useEffect, useRef, useState } from "react";
import type { PublicGameState } from "@grundbesitz/shared";
import { useStore } from "../../lib/store";
import type { EventBatch } from "../../lib/store";
import { STEP_MS, movementSteps, stepDelayMs } from "../../lib/boardLayout";

export interface ShownPosition {
  /** Field the token is drawn on right now. */
  field: number;
  /** The token jumps there instead of sliding (jail, resync). */
  instant: boolean;
  /** Duration of the slide onto this field. */
  stepMs: number;
}

function reducedMotion(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function snapshot(game: PublicGameState): Record<string, ShownPosition> {
  const out: Record<string, ShownPosition> = {};
  for (const p of game.players) out[p.id] = { field: p.position, instant: true, stepMs: STEP_MS };
  return out;
}

/** After a jail jump the queue waits a moment so the move is noticed. */
const JUMP_PAUSE_MS = 320;
/** How long to wait for events before the tokens are set to the authoritative positions. */
const RECONCILE_MS = 280;

/**
 * Where each token is drawn. Walks tokens field by field along `PLAYER_MOVED.path` (one queue per player)
 * and resyncs with `game.players[i].position` whenever no event explains a difference (reload, reconnect).
 */
export function useTokenPositions(game: PublicGameState): Record<string, ShownPosition> {
  const [shown, setShown] = useState<Record<string, ShownPosition>>(() => snapshot(game));
  const shownRef = useRef(shown);
  const gameRef = useRef(game);
  const schedule = useRef<() => void>(() => undefined);

  useEffect(() => {
    const queues = new Map<string, ShownPosition[]>();
    const timers = new Map<string, number>();
    let reconcileTimer: number | undefined;

    const commit = (next: Record<string, ShownPosition>) => {
      shownRef.current = next;
      setShown(next);
    };
    const put = (id: string, pos: ShownPosition) => commit({ ...shownRef.current, [id]: pos });
    const busy = (id: string) => timers.has(id) || (queues.get(id)?.length ?? 0) > 0;

    const reconcile = () => {
      let next = shownRef.current;
      let changed = false;
      for (const p of gameRef.current.players) {
        if (p.eliminated || busy(p.id)) continue;
        if (next[p.id]?.field !== p.position) {
          next = { ...next, [p.id]: { field: p.position, instant: true, stepMs: STEP_MS } };
          changed = true;
        }
      }
      if (changed) commit(next);
    };
    schedule.current = () => {
      window.clearTimeout(reconcileTimer);
      reconcileTimer = window.setTimeout(reconcile, RECONCILE_MS);
    };

    const pump = (id: string) => {
      if (timers.has(id)) return;
      const step = () => {
        const next = queues.get(id)?.shift();
        if (!next) {
          timers.delete(id);
          schedule.current();
          return;
        }
        put(id, next);
        timers.set(id, window.setTimeout(step, next.instant ? JUMP_PAUSE_MS : next.stepMs));
      };
      step();
    };

    const onBatch = (batch: EventBatch) => {
      for (const ev of batch.events) {
        if (ev.type !== "PLAYER_MOVED") continue;
        const { steps, instant } = movementSteps(ev);
        if (steps.length === 0) continue;
        const id = ev.playerId;
        if (reducedMotion()) {
          put(id, { field: steps[steps.length - 1], instant: true, stepMs: STEP_MS });
          continue;
        }
        if (!busy(id) && shownRef.current[id]?.field !== ev.from) put(id, { field: ev.from, instant: true, stepMs: STEP_MS });
        const stepMs = stepDelayMs(steps.length);
        const queue = queues.get(id) ?? [];
        for (const field of steps) queue.push({ field, instant, stepMs });
        queues.set(id, queue);
        pump(id);
      }
      schedule.current();
    };

    const unsubscribe = useStore.getState().subscribeEvents(onBatch);
    return () => {
      unsubscribe();
      window.clearTimeout(reconcileTimer);
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, []);

  useEffect(() => {
    gameRef.current = game;
    schedule.current();
  }, [game]);

  return shown;
}

export interface DiceDisplay {
  dice: [number, number] | null;
  rolling: boolean;
  rollId: number;
}

const ROLL_MS = 780;

/** The dice on the board: tumbles on `DICE_ROLLED`, otherwise shows the last roll of the current turn. */
export function useDiceDisplay(game: PublicGameState): DiceDisplay {
  const [state, setState] = useState<DiceDisplay>({ dice: game.turn.lastRoll, rolling: false, rollId: 0 });

  useEffect(() => {
    let timer: number | undefined;
    const unsubscribe = useStore.getState().subscribeEvents((batch) => {
      for (const ev of batch.events) {
        if (ev.type !== "DICE_ROLLED") continue;
        setState((s) => ({ dice: ev.dice, rolling: !reducedMotion(), rollId: s.rollId + 1 }));
        window.clearTimeout(timer);
        timer = window.setTimeout(() => setState((s) => ({ ...s, rolling: false })), ROLL_MS);
      }
    });
    return () => {
      unsubscribe();
      window.clearTimeout(timer);
    };
  }, []);

  const a = game.turn.lastRoll?.[0];
  const b = game.turn.lastRoll?.[1];
  useEffect(() => {
    if (a === undefined || b === undefined) return;
    setState((s) => (s.rolling || (s.dice && s.dice[0] === a && s.dice[1] === b) ? s : { ...s, dice: [a, b] }));
  }, [a, b]);

  return state;
}
